/**
 * Shared recital token helpers. Mirrors the server-side placeholder system in
 * RecitalPublisher: {auto_*} tokens are computed, {internal_*} are resolved by
 * the backend, and bare {placeholder} tokens are coordinator-provided values.
 *
 * At recital creation we discover the bare placeholders in the create-time
 * templates (titles + opening posts), collect their values, and persist them on
 * the recital so later lifecycle transitions can render titles consistently.
 */

var TOKEN_RE = /\{(\w+)\}/g;
function autoCurrentMonth() {
  try {
    return new Date().toLocaleString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    return '';
  }
}

// Day-level date, e.g. "15 August 2025" — matches the announcement "Update N: <date>" format.
export function formatDay(date) {
  try {
    var d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    return '';
  }
}

// Month-level date, e.g. "August 2025".
export function formatMonth(date) {
  try {
    var d = date instanceof Date ? date : new Date(date);
    return d.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    return '';
  }
}
function autoToday() {
  return formatDay(new Date());
}
function autoTwoMonthsAgo() {
  var d = new Date();
  d.setMonth(d.getMonth() - 2);
  return formatMonth(d);
}

// Snap an estimate to the nearest occurrence of a day-of-month, comparing the
// estimate's own month against the adjacent month in the estimate's direction.
// Ties favor the later (forward) date so a milestone never drifts earlier than
// the closer of the two candidates.
function nearestDayOfMonth(estimate, day) {
  var y = estimate.getFullYear();
  var m = estimate.getMonth();
  var thisMonth = new Date(y, m, day);
  var diff = estimate.getTime() - thisMonth.getTime();
  if (diff === 0) return thisMonth;
  var other = diff > 0 ? new Date(y, m + 1, day) : new Date(y, m - 1, day);
  var dThis = Math.abs(estimate.getTime() - thisMonth.getTime());
  var dOther = Math.abs(estimate.getTime() - other.getTime());
  if (dThis < dOther) return thisMonth;
  if (dOther < dThis) return other;
  // Tie: pick the later of the two candidates.
  return thisMonth.getTime() >= other.getTime() ? thisMonth : other;
}

/**
 * Computed milestone date tokens (auto_ convention: auto-filled but reviewable
 * /overridable in the Create modal, like the publish dialog's auto_ tokens).
 */
export var AUTO_DATE_TOKENS = ['auto_submissions_open_date', 'auto_submissions_open_month', 'auto_golive_date', 'auto_golive_month', 'auto_next_announcement_month'];

/**
 * Compute default milestone dates from a per-type offset config and an anchor
 * (announcement) date. Each milestone is estimated from the anchor by its
 * cumulative offset, then snapped to the NEAREST configured day-of-month:
 * submissions-open on submissions_open_day_of_month (default 1), go-live on
 * golive_day_of_month (default 15). Also returns auto_next_announcement_month —
 * the month next_announcement_months_out (default 2) after the anchor. Returns
 * full-date + month-granularity strings for each milestone.
 */
export function computeMilestoneDates(type, anchorDate) {
  var anchor = anchorDate instanceof Date ? anchorDate : new Date();
  var toOpen = parseInt(type && type.announce_to_open_days, 10);
  var toLive = parseInt(type && type.open_to_golive_days, 10);
  if (!Number.isFinite(toOpen)) toOpen = 14;
  if (!Number.isFinite(toLive)) toLive = 14;
  var openDay = parseInt(type && type.submissions_open_day_of_month, 10);
  if (!Number.isFinite(openDay) || openDay < 1 || openDay > 28) openDay = 1;
  var goliveDay = parseInt(type && type.golive_day_of_month, 10);
  if (!Number.isFinite(goliveDay) || goliveDay < 1 || goliveDay > 28) goliveDay = 15;
  var nextMonthsOut = parseInt(type && type.next_announcement_months_out, 10);
  if (!Number.isFinite(nextMonthsOut) || nextMonthsOut < 0) nextMonthsOut = 2;

  // Submissions-open is estimated at announce + announce_to_open_days and snapped
  // to the nearest submissions_open_day_of_month; go-live at announce +
  // (announce_to_open + open_to_golive) and snapped to the nearest golive_day_of_month.
  var open = nearestDayOfMonth(new Date(anchor.getTime() + toOpen * 86400000), openDay);
  var golive = nearestDayOfMonth(new Date(anchor.getTime() + (toOpen + toLive) * 86400000), goliveDay);

  // The next recital is announced a whole number of months after this anchor;
  // only the month matters, so add months on the 1st to avoid day overflow.
  var nextAnnouncement = new Date(anchor.getFullYear(), anchor.getMonth() + nextMonthsOut, 1);
  return {
    auto_submissions_open_date: formatDay(open),
    auto_submissions_open_month: formatMonth(open),
    auto_golive_date: formatDay(golive),
    auto_golive_month: formatMonth(golive),
    auto_next_announcement_month: formatMonth(nextAnnouncement)
  };
}

/** Which computed auto-date tokens appear in the given templates (order-preserving). */
export function discoverDateTokens(templates) {
  var present = {};
  (templates || []).forEach(function (tpl) {
    var s = String(tpl || '');
    AUTO_DATE_TOKENS.forEach(function (t) {
      if (s.indexOf('{' + t + '}') !== -1) present[t] = true;
    });
  });
  return AUTO_DATE_TOKENS.filter(function (t) {
    return present[t];
  });
}

/**
 * Discover bare {placeholder} names (excluding auto_ and internal_ prefixed
 * tokens) across the given templates. Returns a de-duplicated,
 * order-preserving list.
 */
export function discoverPlaceholders(templates) {
  var seen = {};
  var out = [];
  (templates || []).forEach(function (tpl) {
    var s = String(tpl || '');
    var m;
    TOKEN_RE.lastIndex = 0;
    while ((m = TOKEN_RE.exec(s)) !== null) {
      var name = m[1];
      if (/^(auto_|internal_)/.test(name)) continue;
      if (seen[name]) continue;
      seen[name] = true;
      out.push(name);
    }
  });
  return out;
}

/** Humanize a token name for a form label: piece -> "Piece", auto_golive_date -> "Golive Date". */
export function humanizeToken(name) {
  return String(name || '').replace(/^(auto_|internal_)/, '').replace(/_/g, ' ').replace(/\b\w/g, function (c) {
    return c.toUpperCase();
  });
}

/**
 * Resolve a template string. ctx = { label, number, placeholderValues,
 * internalLinks, pastRecitals }. Computed {auto_*} tokens, code-resolved
 * {internal_*} tokens (link tokens from ctx.internalLinks, {internal_past_recitals}
 * from ctx.pastRecitals), and any coordinator-provided bare placeholders are
 * substituted; unknown tokens are left intact. internalLinks = { meta, recital }
 * maps {internal_*_link} tokens to URLs — the JS mirror of
 * TokenResolver::discussionLink / the past-recitals list.
 */
export function resolveTemplate(template, ctx) {
  ctx = ctx || {};
  var values = ctx.placeholderValues || {};
  var links = ctx.internalLinks || {};
  var internal = {
    internal_meta_link: links.meta,
    internal_recital_link: links.recital
  };
  var out = String(template || '').replace(/\{auto_recital_name\}/g, ctx.label || '').replace(/\{auto_recital_number\}/g, String(ctx.number != null ? ctx.number : '')).replace(/\{auto_current_month\}/g, autoCurrentMonth()).replace(/\{auto_today\}/g, autoToday()).replace(/\{auto_two_months_ago\}/g, autoTwoMonthsAgo());

  // {internal_past_recitals}: a generated list; substitute whenever provided
  // (even empty — an empty list should remove the token, not leave it literal).
  if (ctx.pastRecitals != null) {
    out = out.replace(/\{internal_past_recitals\}/g, String(ctx.pastRecitals));
  }
  return out.replace(TOKEN_RE, function (full, name) {
    if (internal[name] != null && internal[name] !== '') return String(internal[name]);
    if (values[name] != null && values[name] !== '') return String(values[name]);
    return full;
  });
}